import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const useUserRole = () => {
  const { user, loading: authLoading } = useAuth();
  const [isProvider, setIsProvider] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isSalesperson, setIsSalesperson] = useState(false);
  const [isPharmacy, setIsPharmacy] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchRoles = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      const roles = (data ?? []) as Array<{ role: string }>;
      const hasSuper = roles.some((r) => r.role === "super_admin");
      // Super admins implicitly have admin capabilities in the UI as well
      setIsSuperAdmin(hasSuper);
      setIsAdmin(hasSuper || roles.some((r) => r.role === "admin"));
      setIsProvider(roles.some((r) => r.role === "provider"));
      setIsSalesperson(roles.some((r) => r.role === "salesperson"));
      setIsPharmacy(roles.some((r) => r.role === "pharmacy"));
    } catch {
      setIsProvider(false);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsSalesperson(false);
      setIsPharmacy(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user?.id) {
      setIsProvider(false);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsSalesperson(false);
      setIsPharmacy(false);
      setLoading(false);
      return;
    }

    fetchRoles(user.id);
  }, [user?.id, authLoading, fetchRoles]);

  return {
    isProvider,
    isAdmin,
    isSuperAdmin,
    isSalesperson,
    isPharmacy,
    loading: authLoading || loading,
  };
};
